import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trophy, Flame, Target, TrendingUp, Zap, Plus, Camera, X, Upload, User, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TeamLeaderboard = () => {
  const { userId } = useParams();
  const [members, setMembers] = useState([]);
  const [hoveredMember, setHoveredMember] = useState(null);
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [deletingMember, setDeletingMember] = useState(null);
  const [newMember, setNewMember] = useState({ name: "", callSign: "", email: "" });
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchTeamMembers();
  }, [userId]);

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team/${userId}`);
      setMembers(response.data);
    } catch (error) {
      console.error("Error fetching team members:", error);
    }
  };

  const handlePhotoUpload = (e, memberId = null) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Photo must be less than 5MB");
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        
        if (memberId) {
          try {
            await axios.put(`${API}/team/${memberId}`, { photoUrl: base64 });
            toast.success("Photo updated!");
            fetchTeamMembers();
            setEditingMember(null);
          } catch (error) {
            toast.error("Failed to update photo");
          }
        } else {
          setPhotoPreview(base64);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddMember = async () => {
    if (!newMember.name || !newMember.callSign) {
      toast.error("Name and call sign are required");
      return;
    }

    try {
      await axios.post(`${API}/team`, {
        userId,
        name: newMember.name,
        callSign: newMember.callSign,
        email: newMember.email || null,
        photoUrl: photoPreview || null
      });
      
      toast.success(`${newMember.name} added to the team!`);
      setNewMember({ name: "", callSign: "", email: "" });
      setPhotoPreview(null);
      setIsAddingMember(false);
      fetchTeamMembers();
    } catch (error) {
      toast.error("Failed to add team member");
    }
  };

  const handleDeleteMember = async (memberId, memberName) => {
    try {
      await axios.delete(`${API}/team/${memberId}`);
      toast.success(`${memberName} removed from team`);
      setDeletingMember(null);
      fetchTeamMembers();
    } catch (error) {
      toast.error("Failed to delete team member");
    }
  };

  const getRankColor = (rank) => {
    switch(rank) {
      case 1: return "from-yellow-400 via-yellow-500 to-yellow-600";
      case 2: return "from-gray-300 via-gray-400 to-gray-500";
      case 3: return "from-orange-400 via-orange-500 to-orange-600";
      default: return "from-neon-cyan via-teal-500 to-cyan-600";
    }
  };

  const getRankBadge = (rank) => {
    switch(rank) {
      case 1: return "🥇";
      case 2: return "🥈";
      case 3: return "🥉";
      default: return `#${rank}`;
    }
  };

  // Only show placeholders if we have fewer than 2 members
  const showPlaceholders = members.length < 2;
  const placeholdersToShow = showPlaceholders ? 2 - members.length : 0;

  return (
    <Card className="glass-card card-3d holo-border p-6 overflow-hidden relative" data-testid="team-leaderboard">
      <div className="mb-6 relative z-10">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl text-white flex items-center gap-3">
              <Trophy className="h-6 w-6 text-neon-cyan" />
              <span className="text-neon-cyan">Team Leaderboard</span>
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Points: Revenue ($1K = 100pts) + Deals (500pts) + Meetings (50pts) + Calls (10pts) + Emails (5pts)
            </p>
          </div>
          <Button
            onClick={() => setIsAddingMember(true)}
            className="bg-neon-cyan hover:bg-neon-cyan/90 text-black font-semibold"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Member
          </Button>
        </div>
      </div>

      <div className={`grid grid-cols-1 ${members.length === 1 ? 'md:grid-cols-2' : members.length >= 2 ? 'md:grid-cols-2 lg:grid-cols-' + Math.min(members.length, 4) : 'md:grid-cols-2'} gap-4 relative z-10`}>
        {/* Actual Members */}
        {members.map((member, idx) => (
          <div
            key={member.memberId}
            className="relative"
            onMouseEnter={() => setHoveredMember(idx)}
            onMouseLeave={() => setHoveredMember(null)}
            data-testid={`leaderboard-member-${idx}`}
          >
            <div 
              className={`
                glass-card-light rounded-2xl p-4 cursor-pointer
                transition-all duration-500 ease-out
                ${hoveredMember === idx ? 'scale-105 -translate-y-2 shadow-xl shadow-neon-cyan/20' : 'scale-100'}
                group
              `}
              style={{
                transformStyle: 'preserve-3d',
                transform: hoveredMember === idx 
                  ? 'perspective(1000px) rotateY(-3deg) rotateX(3deg) translateZ(30px)'
                  : 'perspective(1000px) rotateY(0deg) rotateX(0deg) translateZ(0px)'
              }}
            >
              {/* Delete button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDeletingMember(member);
                }}
                className="absolute top-2 left-2 p-1.5 rounded-full bg-red-500/20 text-red-400 opacity-0 group-hover:opacity-100 hover:bg-red-500/40 transition-all z-20"
              >
                <Trash2 className="h-4 w-4" />
              </button>

              {/* Rank badge */}
              <div className={`
                absolute -top-3 -right-3 h-10 w-10 rounded-full 
                bg-gradient-to-br ${getRankColor(member.rank)}
                flex items-center justify-center text-white font-bold text-lg
                shadow-lg
              `}>
                {getRankBadge(member.rank)}
              </div>

              {/* Avatar with photo upload */}
              <div className="relative mb-3 flex justify-center">
                <div className="relative group/avatar">
                  <Avatar className={`
                    h-20 w-20 border-4 transition-all duration-500
                    ${hoveredMember === idx ? 'border-neon-cyan/50' : 'border-gray-700'}
                  `}>
                    <AvatarImage 
                      src={member.photoUrl || member.avatar} 
                      alt={member.name}
                    />
                    <AvatarFallback className="bg-neon-cyan/20 text-neon-cyan text-lg font-bold">
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>

                  {/* Photo upload overlay */}
                  <div 
                    className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center opacity-0 group-hover/avatar:opacity-100 transition-opacity cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingMember(member.memberId);
                    }}
                  >
                    <Camera className="h-6 w-6 text-white" />
                  </div>

                  {/* Win streak flame */}
                  {member.winStreak > 0 && (
                    <div className="absolute -top-2 -right-2 bg-gradient-to-br from-orange-500 to-red-600 rounded-full h-7 w-7 flex items-center justify-center shadow-lg">
                      <Flame className="h-4 w-4 text-white" />
                    </div>
                  )}
                </div>
              </div>

              {/* Name & Call Sign */}
              <p className="text-sm font-bold text-white mb-0.5 text-center">{member.name}</p>
              <p className="text-xs text-gray-500 mb-2 text-center">"{member.callSign}"</p>

              {/* Points */}
              <div className="text-center mb-3">
                <p className={`text-2xl font-bold bg-gradient-to-r ${getRankColor(member.rank)} bg-clip-text text-transparent`}>
                  {(member.points || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-400">points</p>
              </div>

              {/* Expanded details on hover */}
              <div className={`
                transition-all duration-500 overflow-hidden
                ${hoveredMember === idx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}
              `}>
                <div className="space-y-2 pt-3 border-t border-gray-700/50">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-400 flex items-center gap-1">
                      <Target className="h-3 w-3" /> Deals
                    </span>
                    <span className="text-white font-bold">{member.dealsClosedThisWeek || 0}</span>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-400 flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" /> Revenue
                    </span>
                    <span className="text-neon-cyan font-bold">
                      ${((member.revenueGenerated || 0) / 1000).toFixed(0)}K
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-400 flex items-center gap-1">
                      <Zap className="h-3 w-3" /> Streak
                    </span>
                    <span className="text-orange-400 font-bold">{member.winStreak || 0} weeks</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Placeholders - only show if less than 2 members */}
        {[...Array(placeholdersToShow)].map((_, i) => (
          <div
            key={`placeholder-${i}`}
            className="glass-card-light rounded-2xl p-4 border-2 border-dashed border-gray-700 flex flex-col items-center justify-center min-h-[200px] cursor-pointer hover:border-neon-cyan/50 transition-colors"
            onClick={() => setIsAddingMember(true)}
            data-testid={`placeholder-${i}`}
          >
            <div className="w-20 h-20 rounded-full bg-gray-800 flex items-center justify-center mb-3">
              <User className="h-10 w-10 text-gray-600" />
            </div>
            <p className="text-gray-500 text-sm mb-2">Add Team Member</p>
            <Button
              size="sm"
              variant="outline"
              className="border-gray-700 text-gray-400 hover:border-neon-cyan hover:text-neon-cyan"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
        ))}
      </div>

      {/* Add Member Dialog */}
      <Dialog open={isAddingMember} onOpenChange={setIsAddingMember}>
        <DialogContent className="glass-card border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold neon-text">Add Team Member</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Photo Upload */}
            <div className="flex justify-center">
              <div 
                className="relative w-24 h-24 rounded-full bg-gray-800 border-2 border-dashed border-gray-600 flex items-center justify-center cursor-pointer hover:border-neon-cyan transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {photoPreview ? (
                  <>
                    <img src={photoPreview} alt="Preview" className="w-full h-full rounded-full object-cover" />
                    <button 
                      className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1"
                      onClick={(e) => { e.stopPropagation(); setPhotoPreview(null); }}
                    >
                      <X className="h-3 w-3 text-white" />
                    </button>
                  </>
                ) : (
                  <div className="text-center">
                    <Upload className="h-8 w-8 text-gray-500 mx-auto mb-1" />
                    <span className="text-xs text-gray-500">Photo</span>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handlePhotoUpload(e)}
              />
            </div>

            <div>
              <label className="text-sm text-gray-400 mb-1 block">Name *</label>
              <Input
                value={newMember.name}
                onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                placeholder="John Smith"
                className="bg-white/5 border-gray-700 text-white"
              />
            </div>

            <div>
              <label className="text-sm text-gray-400 mb-1 block">Call Sign *</label>
              <Input
                value={newMember.callSign}
                onChange={(e) => setNewMember({ ...newMember, callSign: e.target.value })}
                placeholder="The Closer"
                className="bg-white/5 border-gray-700 text-white"
              />
            </div>

            <div>
              <label className="text-sm text-gray-400 mb-1 block">Email</label>
              <Input
                value={newMember.email}
                onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                placeholder="john@company.com"
                className="bg-white/5 border-gray-700 text-white"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsAddingMember(false);
                  setNewMember({ name: "", callSign: "", email: "" });
                  setPhotoPreview(null);
                }}
                className="flex-1 border-gray-700 text-gray-400"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddMember}
                className="flex-1 bg-neon-cyan hover:bg-neon-cyan/90 text-black font-semibold"
              >
                Add Member
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Photo Dialog */}
      <Dialog open={!!editingMember} onOpenChange={() => setEditingMember(null)}>
        <DialogContent className="glass-card border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Update Photo</DialogTitle>
          </DialogHeader>
          
          <div className="flex flex-col items-center gap-4 mt-4">
            <div 
              className="w-32 h-32 rounded-full bg-gray-800 border-2 border-dashed border-gray-600 flex items-center justify-center cursor-pointer hover:border-neon-cyan transition-colors"
              onClick={() => document.getElementById('edit-photo-input')?.click()}
            >
              <div className="text-center">
                <Camera className="h-10 w-10 text-gray-500 mx-auto mb-2" />
                <span className="text-sm text-gray-500">Click to upload</span>
              </div>
            </div>
            <input
              id="edit-photo-input"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handlePhotoUpload(e, editingMember)}
            />
            <p className="text-xs text-gray-500">Max 5MB • JPG, PNG, GIF</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletingMember} onOpenChange={() => setDeletingMember(null)}>
        <DialogContent className="glass-card border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-400">Delete Team Member</DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            <p className="text-gray-300">
              Are you sure you want to remove <strong>{deletingMember?.name}</strong> from the team?
            </p>
            <p className="text-sm text-gray-500 mt-2">This action cannot be undone.</p>
            
            <div className="flex gap-3 pt-6">
              <Button
                variant="outline"
                onClick={() => setDeletingMember(null)}
                className="flex-1 border-gray-700 text-gray-400"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDeleteMember(deletingMember.memberId, deletingMember.name)}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default TeamLeaderboard;
